/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { NotificationService } from 'src/app/notification/notification.service';
import { AuthService } from 'src/app/shared/service/auth.service';
import { RouterService, routerConfig } from 'src/app/shared/service/router.service';
import { ProjectService } from '../dashboard/project/project.service';
import { FormApiValidationError } from '../shared/model/errors/formApiError.model';

export type ErrorNotificationSkip = boolean | number[];

export const ERROR_NOTIFICATION_SKIP = new HttpContextToken<ErrorNotificationSkip>(() => false);

@Injectable()
export class ErrorIntercecptorService implements HttpInterceptor {
  private tokenExpired$: Subject<any> = new Subject();

  private skipErrorNotification = false;

  private dialogRef = inject(MatDialog);
  private notificationService = inject(NotificationService);
  private projectService = inject(ProjectService);
  private routerService = inject(RouterService);
  private authService = inject(AuthService);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const skipConfig = req.context.get(ERROR_NOTIFICATION_SKIP);

    return next.handle(req).pipe(
      takeUntil(this.tokenExpired$),
      catchError((error: HttpErrorResponse) => this.handleHttpError(error, skipConfig)),
    );
  }

  private handleHttpError(error: HttpErrorResponse, skipConfig: ErrorNotificationSkip): Observable<never> {
    const errorMessage = error.error?.message || null;
    const status = error.status;

    if (this.shouldSkipErrorNotification(status, skipConfig)) {
      return throwError(() => error.error);
    }
    switch ( status ) {
      case 400:
        return throwError(error.error);

      case 401:
        this.logout();
        this.tokenExpired$.next(null);
        return throwError(error.error);

      case 403:
      case 404:
        this.notifyError(errorMessage);
        return throwError(error.error);

      case 422: {
        return throwError(new FormApiValidationError(error.error, this.notificationService));
      }
      case 455: // Maintenance
        this.notificationService.error('maintenance_start');
        this.routerService.navigate(routerConfig.maintenance);
        this.tokenExpired$.next(null);
        return throwError(error.error);
      case 460:
        this.notifyError(errorMessage);
        return throwError(error.error);

      case 461:
        this.notifyError(errorMessage);
        return throwError(error.error);

      case 462: // API bussines error
        this.notifyError(errorMessage);
        return throwError(error.error);
      case 463:
        return throwError(error.error);
      case 464: // RESTRICTION_ERROR
        if (this.authService.authUser!.permissions!.some((perm) => perm === 'manage payment')) {
          this.routerService.navigate(routerConfig.paymentsAddProducts);
        }
        this.notifyError(errorMessage);
        return throwError(error.error);
      case 465: // NO_SUBSCRIPTION_ERROR
        this.routerService.navigate(routerConfig.paymentsPlan);
        this.notifyError(errorMessage);
        return throwError(error.error);
      default:
        this.notificationService.error('unknown_error_occurred');
        return throwError(error.error);
    }
  }

  private shouldSkipErrorNotification(status: number, skipConfig: ErrorNotificationSkip): boolean {
    if (skipConfig === true) {
      return true;
    }
    return Array.isArray(skipConfig) && skipConfig.includes(status);
  }

  private notifyError(errorMessage: any) {
    !this.skipErrorNotification && this.notificationService.error(errorMessage, {}, true);
  }

  private logout(errorTranslateKey = 'session_expired', route = routerConfig['/']) {
    this.dialogRef.closeAll();
    this.authService.removeToken();
    this.projectService.setActiveProject(null!);
    this.routerService.navigate(route);
    this.notificationService.error(errorTranslateKey);
  }
}
